import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';

// Material
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

// Shared
import { concatMap, delay, finalize, Observable, tap } from 'rxjs';
import { FileDownloadHelperService } from '../../services/file-download-helper.service';
import { NotificationService } from '../../services/notification.service';
import { QuerySettingService } from '../../services/query-setting.service';
import { ConfirmModalComponent } from '../../shared/confirm-modal/confirm-modal.component';
import { LoadingComponent } from "../../shared/loading/loading.component";
import { ModalComponent } from '../../shared/modal/modal.component';
import { SearchBarComponent } from '../../shared/searchbar/searchbar.component';
import { SharedTableComponent } from '../../shared/shared-table/shared-table.component';
import { UploadFilePanelComponent } from "../../shared/upload-file-panel/upload-file-panel.component";

type LangCode = 'IT' | 'EN' | 'DE' | 'FR' | 'ES';

@Component({
  selector: 'app-synonyms',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    SharedTableComponent,
    SearchBarComponent,
    MatChipsModule,
    MatProgressBarModule,
    MatSortModule,
    UploadFilePanelComponent,
    LoadingComponent
],
  templateUrl: './synonyms.component.html',
  styleUrls: ['./synonyms.component.scss'],
})
export class SynonymsComponent implements OnInit {

  selectedLang: LangCode | '' = '';
  pendingFile: File | null = null;
  isUploading = false;
  isLoading = false;
  hasError = false;
  isLoadingFullScreen = false;

  //salvataggio della lista completa di files (non paginata) per controllo in caso di caricamento duplicati
  allSynonymsFilesList: Set<string> = new Set();

  // ordinamento & paginazione (server-side)
  sortBy: string = 'filename:asc';
  pageIndex = 0;
  pageSize = 10;
  totalItems = 0;

  // filtro
  querySettingFilter = {
    filename: '',
    type: 'SYNONYMS'
  };

  dataSourceSynonyms = new MatTableDataSource<any>([]);

  displayedColumns = ['filename', 'actions'];
  columnLabels: Record<string, string> = { filename: 'Nome' };

  filterFields: string[] = ['filename'];
  filterMap: Record<string, string[]> = { filename: ['filename'] };
  filterLabels: Record<string, string[]> = { filename: ['nome'] };

  constructor(
    private readonly querySettingService: QuerySettingService,
    private readonly snackBar: MatSnackBar,
    private readonly dialog: MatDialog,
    private readonly notificationService: NotificationService,
    private readonly downloadFileHelper: FileDownloadHelperService
  ) {}

  ngOnInit(): void {
    this.getSynonymsList().subscribe();
    this.getAllSynonymsList();
  }

  // ===== API =====
  private getSynonymsList(): Observable<any> {
    this.isLoading = true;
    this.hasError = false;
    return this.querySettingService
      .getQuerySettingList(this.pageIndex, this.pageSize, this.sortBy, this.querySettingFilter)
      .pipe(
        tap({
          next: (response) => {
            this.dataSourceSynonyms.data = response?.content ?? [];
            this.totalItems = response?.totalElements ?? this.dataSourceSynonyms.data.length;
          },
          error: (err) => {
            console.log("errore nel recupero dei dati", err);
            this.hasError = true;
          }
        }), 
        finalize(() => { 
          this.isLoading = false;
        })
      );
  }

  private getAllSynonymsList(): void {
    this.querySettingService
      .getAllQuerySettings(this.querySettingFilter)
      .subscribe({
        next: (response) => {
          this.allSynonymsFilesList = new Set(response?.map((item:any) => item.filename) ?? []);
        },
        error: (err) => {
          console.log("errore nel recupero dei dati", err);
        }
      });
  }

  // ===== Paginazione =====
  onPageChanged(event: { pageIndex: number; pageSize: number }): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.getSynonymsList().subscribe();
  }

  // ===== Ordinamento dall’header (matSortChange) =====
  onSortChanged(event: Sort): void {
    if (!event.active || !event.direction) {
      this.sortBy = 'filename:asc'; // fallback
    } else {
      const fieldMap: Record<string, string> = {
        filename: 'filename',
      };
      const beField = fieldMap[event.active] ?? event.active;
      this.sortBy = `${beField}:${event.direction}`;
    }
    this.pageIndex = 0; // reset pagina quando cambia l’ordinamento
    this.getSynonymsList().subscribe();
  }

  // ===== Filtro (da search-bar) =====
  onFilterChanged(filters: Record<string, string>): void {
    this.querySettingFilter.filename = (filters['filename'] ?? '').toString();
    this.pageIndex = 0; // riparti dalla prima pagina
    this.getSynonymsList().subscribe();
  }

  onLangSelected(lang: LangCode | ''): void {
    this.selectedLang = lang;
  }
  
  onFileSelected(file: File | null): void {
    this.pendingFile = file;
  }
  
  removeFile(): void {
    this.pendingFile = null;
  } 

  onUpload(event: {file: File; lang: LangCode}): void {
    
    const {file, lang} = event;

    const fname = file.name;
    
    if (this.allSynonymsFilesList.has(fname)) {
      this.dialog.open(ConfirmModalComponent, {
        width: '400px',
        data: {
          message: `Esiste già un file con il nome ${fname}.`,
          title: 'Attenzione'
        }
      });
      return;
    }

    this.isUploading = true;
    this.isLoadingFullScreen = true;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('lang', lang);
    formData.append('type', this.querySettingFilter.type);

    // debug
    formData.forEach((value, key) => console.log(`${key}:`, value));

    this.querySettingService.addNewQuerySetting(formData)
      .pipe(
        tap(() => {
          console.log('Upload completato!');
          this.allSynonymsFilesList.add(fname); //aggiorno la lista non paginata
        }),
        delay(5000),
        concatMap(() => this.querySettingService.reloadSearchAnalyzers()),
        tap(() => {
          console.log('Reload success!');
        }),
        concatMap(()=>
          // ricarico mantenendo sort & pagina
          this.getSynonymsList()),
        finalize(() => {
          this.isUploading = false;
          this.selectedLang = '';
          this.removeFile();
          this.isLoadingFullScreen = false;
        })
      )
      .subscribe({
        next: () => {
          this.notificationService.successOnUpload(fname);
        },
        error: (err) => {
          console.log('Errore durante il caricamento', err);
          this.notificationService.errorOnUpload(fname);
        }
      });
  }

  // ===== Modale azioni riga =====
  openDialog(event: { data: any; type: 'add' | 'edit' | 'delete' }) {
    const dialogRef = this.dialog.open(ModalComponent, {
      width: "400px",
      data: {
        action: event.type,
        payload: event.data
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && event.type === "delete") {
        this.isLoadingFullScreen = true;
        this.querySettingService.deleteQuerySetting(result.id).pipe(
          tap(() => {
            this.allSynonymsFilesList.delete(result.filename); // aggiorno la lista non paginata
          }),
          concatMap(()=> this.getSynonymsList()),
          finalize(()=> { this.isLoadingFullScreen = false; })).subscribe({
          next: () => {
              this.notificationService.entitySuccess('delete', `File: ${result.filename}`)
          },
          error: (err) => {
            console.log("errore nella richiesta", err);
            this.notificationService.entityError('delete', `${result.filename}`);
          },
        });
      }
    });
  }

  downloadFile(file: any){
    console.log('file',file);
    this.isLoadingFullScreen = true;
    this.downloadFileHelper.downloadFile(file, () => {
      this.isLoadingFullScreen = false;
    })

  }
}